import { useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Route } from '../../../../route-constants';
import { AppStateValue } from '../../../AppState';
import { getCachedUsers } from '../../../shared/cache/PlatformUserCache';
import { getCachedSlaDefinitions } from '../../../shared/cache/SlaDefinitionCache';
import { platformService } from '../../../shared/services/platform.service';
import { FILTERS_STRINGS, MyWorkListViewType } from '../../../shared/utils/constants';
import { chunk } from '../../../shared/utils/helper';
import { DEFAULT_SORT_ORDER_FOR_QUERYING, getCustomPropertyQueryValue } from '../../List/ListFunctions';
import { EnumSortOrderType } from '../../toolbar/CustomViewContextProvider';
import { MyWorkContext } from '../MyWorkContextProvider';
import { getMyWorkSortOrderFromStorage, getSelectedTabFromStorage, getShowClosedFromStorage } from './myWorkListHelper';

const api = new platformService();

// Caps the TicketId IN-list size per TicketSla request regardless of configured page size (25/50/100)
const SLA_ID_BATCH_SIZE = 25;

const useMyWorkListFunctions = (currentState: AppStateValue) => {
  const { myWorkStates, setMyWorkStates } = useContext(MyWorkContext);
  const location = useLocation();
  const stateRef = useRef(myWorkStates);

  const isMyGroupWork = location.pathname.includes(Route.myGroupWork.pagePath);
  
  const moveToPage = (action: string) => {
    if (action === 'next') {
      setMyWorkStates(prev => ({
        ...prev,
        myWorkList: {
          ...prev.myWorkList,
          currentPage: prev.myWorkList.currentPage + 1
        }
      }));
      getpaginatedMyWorkList({ page: myWorkStates.myWorkList.currentPage + 1, selectedTabNumber: myWorkStates.myWorkList.selectedTabId });
    } else if (action === 'previous') {
      setMyWorkStates(prev => ({
        ...prev,
        myWorkList: {
          ...prev.myWorkList,
          currentPage: prev.myWorkList.currentPage - 1
        }
      }));
      getpaginatedMyWorkList({ page: myWorkStates.myWorkList.currentPage - 1, selectedTabNumber: myWorkStates.myWorkList.selectedTabId });
    } else {
      const thisPage = parseInt(action) || 1
      setMyWorkStates(prev => ({
        ...prev,
        myWorkList: {
          ...prev.myWorkList,
          currentPage: thisPage
        }
      }));
      getpaginatedMyWorkList({ page: thisPage, selectedTabNumber: myWorkStates.myWorkList.selectedTabId });
    }
  };
  const countPerPage = (count: number) => {
    localStorage.setItem('ticketListItemPerPage', count.toString());
    setMyWorkStates(prev => ({ ...prev, myWorkList: { ...prev.myWorkList, countPerPage: count } }));
    getpaginatedMyWorkList({ page: myWorkStates.myWorkList.currentPage, count: count, selectedTabNumber: myWorkStates.myWorkList.selectedTabId });
  }

  const MYWORK_LIST_COLUMNS_QUERY_VALUE: { [key in ISortOrderBy]: string } = {
    0: 'Icon',
    1: 'Id',
    2: 'Title',
    3: 'Assignee',
    4: 'StatusName',
    5: 'GroupChat',
    6: 'RequesterName',
    7: 'TeamName',
    8: 'SupportGroupName',
    9: 'TypeName',
    10: 'CategoryName',
    11: 'PriorityName',
    12: 'LastModified',
    13: 'CreatedDate',
    14: 'DueDate',
    15: 'ResolutionDate',
    16: 'LifecycleName',
    17: 'LifecyclePhaseName',
    18: 'Sla',
    19: 'AffectedUsers',
    20: 'Collaborators',
    21: 'ResolutionCategory',
    22: 'Note',
    23: 'InternalNote',
    24: 'DraftKB'    
  };

  const _getpaginatedTicketsPromise = async ({ page, count, selectedTabNumber, showClosed, sortOrder }: IGetpaginatedMyWorkList) => {
    const skip = page * count - count;

    const isClosed = getShowClosedFromStorage(isMyGroupWork) ?? showClosed ?? myWorkStates.showClosed;
    const selectedTabId = getSelectedTabFromStorage(isMyGroupWork) ?? selectedTabNumber ?? Number(MyWorkListViewType.All) as MyWorkListDataType;
    sortOrder = getMyWorkSortOrderFromStorage(isMyGroupWork, selectedTabId) || sortOrder || myWorkStates.sortOrder[selectedTabId] || DEFAULT_SORT_ORDER_FOR_QUERYING;
    const orderBy = sortOrder.orderBy;
    const orderType = sortOrder.orderType;

    let orderByString = MYWORK_LIST_COLUMNS_QUERY_VALUE[orderBy];
    if(!orderByString)
      orderByString = getCustomPropertyQueryValue(currentState?.customProperties, MYWORK_LIST_COLUMNS_QUERY_VALUE, orderBy);

    return api.getMyWorkListData(`(ViewTypeKey=${selectedTabId},IsClosed=${isClosed},IsGroupWork=${isMyGroupWork})?$orderby=${orderByString} ${EnumSortOrderType[orderType]}&$skip=${skip}&$top=${count}`);
  };

  const loadUsers = async (data: any[]) => {
    const users = data.map(x => [x.RequesterId, x.Assignee, ...(x.AffectedUsers ?? []), ...(x.Collaborators ?? [])]).flat().filter(x => x && x !== 0);
    return await getCachedUsers(users);
  }
  const attachSlas = async (data: any[]) => {
    try {
      const ticketIds = data.filter(x => x.ItemType === MyWorkListViewType.Tickets).map(x => x.Id);
      const slas = (await Promise.all(chunk(ticketIds, SLA_ID_BATCH_SIZE).map(ids =>
        api.getTicketSla(`${FILTERS_STRINGS.TICKET_SLA_FILTER_LEAN}&$filter=TicketId in (${ids.join(',')})`)
      ))).flat();

      const slaDefinitions = await getCachedSlaDefinitions(slas.map(s => s.ServiceLevelAgreementId));
      slas.forEach(s => { s.ServiceLevelAgreement = slaDefinitions.get(s.ServiceLevelAgreementId); });

      data.forEach(x => {
        if (x.ItemType === MyWorkListViewType.Tickets) {
          x.TicketSlas = slas.filter(s => s.TicketId === x.Id);
        }
      });
      setMyWorkStates(prev => ({
        ...prev,
        myWorkList: {
          ...prev.myWorkList,
          allData: data
        }
      }));
    } catch (e) {
      console.error(`Error attaching SLAs`, e);
    }
  };

  const getpaginatedMyWorkList = async ({
    page = 1,
    count,
    selectedTabNumber,
    showClosed,
    sortOrder,
    hideLoading
  }: IGetpaginatedMyWorkList) => {
    try {
      if (!hideLoading) {
        setMyWorkStates(prev => ({
          ...prev,
          myWorkList: {
            ...prev.myWorkList,
            isFetching: true
          }
        }));
      }
      count ??= myWorkStates.myWorkList.countPerPage;

      const data = (await _getpaginatedTicketsPromise({ page, count, selectedTabNumber, showClosed, sortOrder }))?.data;
      const { CountApprovals = 0, CountTickets = 0, CountTasks = 0 } = data.ItemsCount || {};
      const list = JSON.parse(data.Data);

      const users = await loadUsers(list);
      list.forEach(t => {
        t.Requester = users.find(u => u.Id == t.RequesterId);
        t.AssigneeUsers = [users.find(u => u.Id == t.Assignee)];
        t.AffectedPlatformUsers = t.AffectedUsers?.map(a => users.find(u => u.Id == a)) ?? []
        t.TicketCollaborators = t.Collaborators?.map(a => users.find(u => u.Id == a)) ?? []
      });

      attachSlas(list);
      setMyWorkStates(prev => ({
        ...prev,
        myWorkList: {
          ...prev.myWorkList,
          isFetching: false,
          allData: list,
          dataCount: {
              [MyWorkListViewType.All]: CountTickets + CountTasks + CountApprovals,
              [MyWorkListViewType.Tickets]: CountTickets,
              [MyWorkListViewType.Tasks]: CountTasks,
              [MyWorkListViewType.Approvals]: CountApprovals,
          }
        }
      }));
    } catch (error) {
      console.error('Error occurred in fetching tickets', error);
    }
  };

  const setMyWorkListInterval = () => setInterval(() => {
    getpaginatedMyWorkList({ 
      page: stateRef.current.myWorkList.currentPage, 
      count: stateRef.current.myWorkList.countPerPage,
      selectedTabNumber: stateRef.current.myWorkList.selectedTabId, 
      showClosed: stateRef.current.showClosed, 
      sortOrder: stateRef.current.sortOrder[stateRef.current.myWorkList.selectedTabId],
      hideLoading: true
    });
  }, 300000);

  return {
    moveToPage,
    countPerPage,
    getpaginatedMyWorkList,
    setMyWorkListInterval
  };
};

export default useMyWorkListFunctions;
